import { CSSObject } from '@emotion/react'
import {
  Tab,
  TabProps,
  TabList,
  TabListProps,
  TabPanel,
  TabPanelProps,
  Tabs as ReactTabs,
  TabsProps,
} from 'react-tabs'
import type { WithMultipleStyleOverrides, WithTheme } from '@opallabs/lapidary'
import { getComponentClassName, rootClassName } from './classNames'
import {
  listStyles,
  panelStyles,
  tabContentStyles,
  tabsStyles,
  tabStyles,
} from './styles'

interface TabListWrapperProps {
  styleOverrides?: {
    tabList: CSSObject
  }
}

const TabListWrapper: React.FC<
  TabListWrapperProps & Omit<TabListProps, 'ref'>
> & { tabsRole: 'TabList' } = ({
  children,
  styleOverrides = { tabList: {} },
  ...tabListProps
}) => (
  <TabList
    css={[listStyles, styleOverrides.tabList]}
    className={getComponentClassName('list')}
    {...tabListProps}
  >
    {children}
  </TabList>
)
TabListWrapper.tabsRole = 'TabList'

interface TabWrapperProps {
  disabled?: boolean
  styleOverrides?: {
    baseTab: CSSObject
    selectedTab?: CSSObject
    disabledTab?: CSSObject
  }
}

const TabWrapper: React.FC<TabWrapperProps & Omit<TabProps, 'ref'>> & {
  tabsRole: 'Tab'
} = ({
  children,
  styleOverrides = { baseTab: {}, selectedTab: {}, disabledTab: {} },
  ...tabProps
}) => {
  const selectedOverrides = {
    [`&.${getComponentClassName('tab')}--selected`]: {
      ...styleOverrides.selectedTab,
    },
  }

  const disabledOverrides = {
    [`&.${getComponentClassName('tab')}--disabled`]: {
      ...styleOverrides.disabledTab,
    },
  }

  return (
    <Tab
      css={[
        tabStyles,
        styleOverrides.baseTab,
        selectedOverrides,
        disabledOverrides,
      ]}
      className={getComponentClassName('tab')}
      {...tabProps}
    >
      <div css={tabContentStyles}>{children}</div>
    </Tab>
  )
}
TabWrapper.tabsRole = 'Tab'

type TabPanelWrapperProps = Readonly<
  Omit<TabPanelProps, 'ref'> &
    WithMultipleStyleOverrides<'tabPanel'> &
    WithTheme
>

const TabPanelWrapper: React.FC<TabPanelWrapperProps> & {
  tabsRole: 'TabPanel'
} = ({
  children,
  styleOverrides = { tabPanel: {} },
  theme = 'light',
  ...tabPanelProps
}) => (
  <TabPanel
    css={[panelStyles({ theme }), styleOverrides.tabPanel]}
    className={getComponentClassName('panel')}
    {...tabPanelProps}
  >
    {children}
  </TabPanel>
)
TabPanelWrapper.tabsRole = 'TabPanel'

interface TabsComponent {
  List: React.FC<React.PropsWithChildren<TabListWrapperProps>> & {
    tabsRole: 'TabList'
  }
  Tab: React.FC<React.PropsWithChildren<TabWrapperProps>> & { tabsRole: 'Tab' }
  Panel: React.FC<TabPanelWrapperProps> & { tabsRole: 'TabPanel' }
}

interface Props extends TabsProps {
  styleOverrides?: {
    tabsWrapper: CSSObject
  }
}

const Tabs: TabsComponent & React.FC<Props> = ({
  children,
  styleOverrides = { tabsWrapper: {} },
  ...tabsProps
}) => (
  <ReactTabs
    css={[tabsStyles, styleOverrides.tabsWrapper]}
    className={rootClassName}
    disabledTabClassName={`${getComponentClassName('tab')}--disabled`}
    selectedTabClassName={`${getComponentClassName('tab')}--selected`}
    selectedTabPanelClassName={`${getComponentClassName('panel')}--selected`}
    {...tabsProps}
  >
    {children}
  </ReactTabs>
)

Tabs.List = TabListWrapper
Tabs.Tab = TabWrapper
Tabs.Panel = TabPanelWrapper

export { Tabs }
